import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { BOARD_TYPES } from "@/lib/constants";
import { createProject, deleteProject } from "@/lib/api";
import {
  LayoutDashboard,
  ChevronRight,
  FolderKanban,
  Settings,
  Users,
  ChevronDown,
  Globe,
  Plus,
  Trash2,
} from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

export default function Sidebar({ projects, currentUser, onProjectCreated }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedProjects, setExpandedProjects] = useState({});
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [newProject, setNewProject] = useState({ name: "", key: "", description: "" });
  const [creating, setCreating] = useState(false);

  const toggleProject = (key) => {
    setExpandedProjects((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    if (!newProject.name.trim() || !newProject.key.trim()) return;
    setCreating(true);
    try {
      await createProject(newProject);
      toast.success("Project created");
      setShowCreateProject(false);
      setNewProject({ name: "", key: "", description: "" });
      onProjectCreated?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      let errorMsg = "Failed to create project";
      if (typeof detail === "string") {
        errorMsg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        errorMsg = detail[0]?.msg || "Validation error";
      }
      toast.error(errorMsg);
    } finally {
      setCreating(false);
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const handleDeleteProject = async () => {
    if (!deleteTarget || deleteConfirmText.toLowerCase() !== "delete") return;
    setDeleting(true);
    try {
      await deleteProject(deleteTarget.id);
      toast.success(`Project "${deleteTarget.name}" deleted`);
      setDeleteTarget(null);
      setDeleteConfirmText("");
      onProjectCreated?.();
      if (location.pathname.includes(`/project/${deleteTarget.key}`)) {
        navigate("/dashboard");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete project");
    } finally {
      setDeleting(false);
    }
  };

  const isActive = (path) => location.pathname === path;
  const isBoardActive = (projectKey, boardKey) =>
    location.pathname === `/project/${projectKey}/${boardKey}`;

  return (
    <div
      className="w-64 border-r border-brand/10 bg-[#F7F5F3] flex flex-col flex-shrink-0"
      data-testid="sidebar"
    >
      {/* Logo */}
      <div className="h-14 border-b border-brand/10 flex items-center px-5 gap-3">
        <img
          src={`${process.env.PUBLIC_URL}/logo.png`}
          alt="Organon"
          className="h-7 w-7 rounded-sm"
        />
        <span
          className="text-lg font-black tracking-tight text-brand"
          style={{ fontFamily: "Chivo, sans-serif" }}
        >
          Organon
        </span>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-3 space-y-1">
          {/* Dashboard */}
          <button
            data-testid="sidebar-dashboard"
            onClick={() => navigate("/dashboard")}
            className={`sidebar-item w-full flex items-center gap-2.5 px-3 py-2 rounded-sm text-sm font-medium ${
              isActive("/dashboard")
                ? "bg-brand text-white"
                : "text-brand hover:bg-brand/10 hover:text-brand"
            }`}
          >
            <LayoutDashboard className="w-4 h-4" />
            Dashboard
          </button>

          {/* Cross Project */}
          <button
            data-testid="sidebar-cross-project"
            onClick={() => navigate("/cross-project")}
            className={`sidebar-item w-full flex items-center gap-2.5 px-3 py-2 rounded-sm text-sm font-medium ${
              isActive("/cross-project")
                ? "bg-brand text-white"
                : "text-brand hover:bg-brand/10 hover:text-brand"
            }`}
          >
            <Globe className="w-4 h-4" />
            Cross Project
          </button>

          {/* Projects */}
          <div className="pt-4">
            <div className="flex items-center justify-between px-3 pb-2">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500">
                Projects
              </p>
              {currentUser?.role === "ADMIN" && (
                <button
                  data-testid="sidebar-create-project"
                  onClick={() => setShowCreateProject(true)}
                  className="text-gray-400 hover:text-black transition-colors"
                  title="Create project"
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            {projects.map((project) => (
              <div key={project.id} className="group/project">
                <div className="flex items-center">
                  <button
                    data-testid={`sidebar-project-${project.key}`}
                    onClick={() => toggleProject(project.key)}
                    className="sidebar-item flex-1 flex items-center gap-2 px-3 py-2 rounded-sm text-sm font-medium text-brand hover:bg-brand/10 hover:text-brand"
                  >
                    <FolderKanban className="w-4 h-4 flex-shrink-0" />
                    <span className="flex-1 text-left truncate">{project.name}</span>
                    {expandedProjects[project.key] ? (
                      <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
                    )}
                  </button>
                  {currentUser?.role === "ADMIN" && (
                    <button
                      data-testid={`sidebar-delete-project-${project.key}`}
                      onClick={(e) => { e.stopPropagation(); setDeleteTarget(project); setDeleteConfirmText(""); }}
                      className="p-1 mr-1 opacity-0 group-hover/project:opacity-100 hover:bg-red-50 rounded-sm transition-all"
                      title="Delete project"
                    >
                      <Trash2 className="w-3.5 h-3.5 text-gray-400 hover:text-red-600" />
                    </button>
                  )}
                </div>
                {expandedProjects[project.key] && (
                  <div className="ml-4 border-l border-gray-200 pl-2 space-y-0.5 mt-0.5">
                    {BOARD_TYPES.map((board) => (
                      <button
                        key={board.key}
                        data-testid={`sidebar-board-${project.key}-${board.key}`}
                        onClick={() => navigate(`/project/${project.key}/${board.key}`)}
                        className={`sidebar-item w-full text-left px-3 py-1.5 rounded-sm text-xs font-medium ${
                          isBoardActive(project.key, board.key)
                            ? "bg-brand text-white"
                            : "text-brand/70 hover:bg-brand/10 hover:text-brand"
                        }`}
                      >
                        {board.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Admin section */}
          {currentUser?.role === "ADMIN" && (
            <div className="pt-4">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 px-3 pb-2">
                Admin
              </p>
              <button
                data-testid="sidebar-admin-users"
                onClick={() => navigate("/admin/users")}
                className={`sidebar-item w-full flex items-center gap-2.5 px-3 py-2 rounded-sm text-sm font-medium ${
                  location.pathname === "/admin/users"
                    ? "bg-brand text-white"
                    : "text-brand hover:bg-brand/10 hover:text-brand"
                }`}
              >
                <Users className="w-4 h-4" />
                Users
              </button>
              <button
                data-testid="sidebar-admin-settings"
                onClick={() => navigate("/admin/slack")}
                className={`sidebar-item w-full flex items-center gap-2.5 px-3 py-2 rounded-sm text-sm font-medium ${
                  location.pathname === "/admin/slack"
                    ? "bg-brand text-white"
                    : "text-brand hover:bg-brand/10 hover:text-brand"
                }`}
              >
                <Settings className="w-4 h-4" />
                Slack Settings
              </button>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Create Project Dialog */}
      <Dialog open={showCreateProject} onOpenChange={setShowCreateProject}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create Project</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateProject} className="space-y-4 pt-2">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1 block">Name</label>
              <input
                data-testid="create-project-name"
                className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-rose"
                placeholder="e.g. Apollo Platform"
                value={newProject.name}
                onChange={(e) => setNewProject((p) => ({ ...p, name: e.target.value }))}
                required
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1 block">Key</label>
              <input
                data-testid="create-project-key"
                className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm uppercase focus:outline-none focus:ring-1 focus:ring-brand-rose"
                placeholder="e.g. AP"
                maxLength={10}
                value={newProject.key}
                onChange={(e) => setNewProject((p) => ({ ...p, key: e.target.value.toUpperCase() }))}
                required
              />
              <p className="text-xs text-gray-400 mt-1">Letters only, used as issue prefix</p>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1 block">Description</label>
              <textarea
                data-testid="create-project-description"
                className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-rose resize-none"
                rows={2}
                placeholder="Brief project description"
                value={newProject.description}
                onChange={(e) => setNewProject((p) => ({ ...p, description: e.target.value }))}
              />
            </div>
            <button
              data-testid="create-project-submit"
              type="submit"
              disabled={creating || !newProject.name.trim() || !newProject.key.trim()}
              className="w-full bg-brand text-white py-2 rounded-sm text-sm font-medium hover:bg-brand-dark disabled:opacity-50 transition-colors"
            >
              {creating ? "Creating..." : "Create Project"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Project Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) { setDeleteTarget(null); setDeleteConfirmText(""); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Project</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-gray-700">
              This will permanently delete <span className="font-bold text-black">{deleteTarget?.name}</span> and all its issues, initiatives, bugs, and comments.
            </p>
            <p className="text-sm text-gray-500">
              Type <span className="font-mono font-bold text-black bg-gray-100 px-1.5 py-0.5 rounded">delete</span> to confirm.
            </p>
            <input
              data-testid="delete-project-confirm-input"
              className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-red-500"
              placeholder="Type delete to confirm"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleDeleteProject()}
              autoFocus
            />
            <div className="flex gap-3">
              <button
                data-testid="delete-project-cancel"
                onClick={() => { setDeleteTarget(null); setDeleteConfirmText(""); }}
                className="flex-1 border border-gray-300 py-2 rounded-sm text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                data-testid="delete-project-submit"
                onClick={handleDeleteProject}
                disabled={deleting || deleteConfirmText.toLowerCase() !== "delete"}
                className="flex-1 bg-red-600 text-white py-2 rounded-sm text-sm font-medium hover:bg-red-700 disabled:opacity-40 transition-colors"
              >
                {deleting ? "Deleting..." : "Delete Project"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
